import { useEffect, useState } from "react";
import { ShieldCheck, LogOut } from "lucide-react";
import { ops, apiError, getToken, setToken, clearToken } from "./opsApi";
import { inputCls, Field, Btn, Msg } from "./opsUi";
import OpsDashboard from "./OpsDashboard";
import TrainerDashboard from "./TrainerDashboard";
import StudentDashboard from "./StudentDashboard";

const ROLE_LABEL = { admin: "Admin", service_manager: "Service Manager", trainer: "Trainer", student: "Student" };

const Login = ({ onLogin }) => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const data = await ops.post("/auth/login", { identifier, password });
      setToken(data.token);
      onLogin(data.user);
    } catch (err) {
      setMsg({ ok: false, text: apiError(err) });
    }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-md px-5 py-16">
      <div className="rounded-2xl border border-gray-100 bg-white p-7 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-dwc-green/10"><ShieldCheck className="h-5 w-5 text-dwc-green" /></span>
          <div>
            <h1 className="text-xl font-black text-dwc-charcoal">DWC Portal</h1>
            <p className="text-xs font-semibold text-dwc-charcoal/50">Admin · Service Manager · Trainer · Student</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="portal-login-form">
          <Field label="Mobile number (or admin email)">
            <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="10-digit mobile" className={inputCls} data-testid="portal-login-identifier" required />
          </Field>
          <Field label="Password">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className={inputCls} data-testid="portal-login-password" required />
          </Field>
          <Msg msg={msg} />
          <button type="submit" disabled={busy} data-testid="portal-login-btn"
            className="w-full rounded-full bg-dwc-green py-3 text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-dwc-green/90 disabled:opacity-50">
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="mt-4 text-center text-[11px] font-semibold text-dwc-charcoal/45">
          Accounts are created by DWC Admin. Contact support if you can't log in.
        </p>
      </div>
    </div>
  );
};

export default function PortalApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    ops.get("/auth/me")
      .then((u) => setUser(u))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const logout = () => { clearToken(); setUser(null); };

  if (checking) return <div className="py-40 text-center text-sm font-bold text-dwc-charcoal/50">Loading portal...</div>;
  if (!user) return <div className="min-h-[70vh] bg-dwc-soft/40 pt-24 md:pt-28"><Login onLogin={setUser} /></div>;

  return (
    <div className="min-h-[70vh] bg-dwc-soft/40 pb-14 pt-24 md:pt-28" data-testid="portal-shell">
      <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-dwc-charcoal">
              {user.role === "student" ? "My Training" : user.role === "trainer" ? "Trainer Dashboard" : "Service Operations"}
            </h1>
            <p className="text-xs font-bold text-dwc-charcoal/50" data-testid="portal-user-info">
              {user.name} · {ROLE_LABEL[user.role]}
            </p>
          </div>
          <Btn variant="outline" small onClick={logout} testId="portal-logout-btn"><LogOut className="mr-1 inline h-3 w-3" />Logout</Btn>
        </div>
        <div className="mt-5">
          {(user.role === "admin" || user.role === "service_manager") && <OpsDashboard user={user} />}
          {user.role === "trainer" && <TrainerDashboard user={user} />}
          {user.role === "student" && <StudentDashboard user={user} />}
        </div>
      </div>
    </div>
  );
}
